import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import { Button } from '../components/Button';
import { OptionModal } from '../components/OptionModal';
import { useToast } from '../components/Toast';
import { Plus, Edit, Trash2, Copy, RefreshCw, Download } from 'lucide-react';
// MIGRATION FIREBASE: Utilise Firebase direct au lieu de l'API Python
import { optionsService, choiceLibraryService, syncAllLibraryToOptions, productsService } from '../services/firebaseService';

export const Options = () => {
  const toast = useToast();
  const [options, setOptions] = useState([]);
  const [products, setProducts] = useState([]); // Pour détecter options non utilisées
  const [showModal, setShowModal] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncingFromLibrary, setSyncingFromLibrary] = useState(false);
  const [showUnusedOptions, setShowUnusedOptions] = useState(false); // Filtre options non utilisées

  useEffect(() => {
    loadOptions();
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const result = await productsService.getAll();
      setProducts(result.products || []);
    } catch (error) {
      console.error('Erreur chargement produits:', error);
    }
  };

  // Vérifier si une option est utilisée dans au moins un produit
  const isOptionUsed = (optionId) => {
    return products.some(p => p.option_ids?.includes(optionId));
  };

  // Options filtrées
  const filteredOptions = showUnusedOptions
    ? options.filter(opt => !isOptionUsed(opt.id))
    : options;

  // FIX Build 214: Sync Bibliothèque → Options (mettre à jour les images depuis la bibliothèque)
  // FIX Build 215: Inclut les sous-options
  const handleSyncFromLibrary = async () => {
    if (!window.confirm('Mettre à jour les images des options depuis la bibliothèque ?\n\nCela va synchroniser TOUTES les options ET sous-options avec les images de la bibliothèque de choix.\n\nOuvrez la console (F12) pour voir les détails.')) return;

    setSyncingFromLibrary(true);
    try {
      console.log('=== DÉBUT SYNC (incluant sous-options) ===');
      const result = await syncAllLibraryToOptions();
      console.log('=== RÉSULTAT SYNC ===', result);

      if (result.totalUpdated > 0) {
        // FIX Build 215: Afficher le détail avec sous-options
        const subOptText = result.subOptionsUpdated > 0 ? ` (dont ${result.subOptionsUpdated} sous-options)` : '';
        toast.success('Synchronisation terminée', `${result.totalUpdated} images mises à jour${subOptText} dans ${result.optionsUpdated} options`);
        await loadOptions(); // Recharger les options
      } else if (result.notFoundInLibrary && result.notFoundInLibrary.length > 0) {
        toast.error('Attention', `${result.notFoundInLibrary.length} choix non trouvés dans la bibliothèque. Voir console (F12).`);
      } else {
        toast.success('Déjà à jour', 'Toutes les images (options + sous-options) sont déjà synchronisées');
      }
    } catch (error) {
      console.error('Erreur sync from library:', error);
      toast.error('Erreur', `Erreur: ${error.message}`);
    } finally {
      setSyncingFromLibrary(false);
    }
  };

  // Synchroniser tous les choix des options vers la bibliothèque
  const handleSyncLibrary = async () => {
    if (!window.confirm('Synchroniser tous les choix des options vers la bibliothèque ?\n\nCela ajoutera les choix manquants et mettra à jour les existants.')) return;

    setSyncing(true);
    let added = 0;
    let updated = 0;
    let errors = 0;

    try {
      // Récupérer la bibliothèque actuelle
      const libraryResult = await choiceLibraryService.getAll();
      const library = libraryResult.choices || [];
      const libraryMap = new Map(
        library.map(c => [c.name.toLowerCase().trim(), c])
      );

      // Parcourir toutes les options et leurs choix
      for (const option of options) {
        for (const choice of option.choices || []) {
          if (!choice.name || !choice.name.trim()) continue;

          const key = choice.name.toLowerCase().trim();
          const existing = libraryMap.get(key);

          try {
            if (existing) {
              // Vérifier si mise à jour nécessaire
              const hasChanges =
                existing.default_price !== choice.price ||
                existing.image_url !== (choice.image_url || null);

              if (hasChanges) {
                await choiceLibraryService.update(existing.id, {
                  name: choice.name,
                  default_price: choice.price,
                  image_url: choice.image_url || null
                });
                updated++;
              }
            } else {
              // Créer nouveau choix
              await choiceLibraryService.create({
                name: choice.name,
                default_price: choice.price,
                image_url: choice.image_url || null,
                description: null
              });
              added++;
              // Ajouter au map pour éviter les doublons
              libraryMap.set(key, { name: choice.name });
            }
          } catch (err) {
            console.error(`Erreur sync choix "${choice.name}":`, err);
            errors++;
          }
        }
      }

      if (added > 0 || updated > 0) {
        toast.success('Synchronisation terminée', `${added} ajouté(s), ${updated} mis à jour`);
      } else {
        toast.success('Bibliothèque à jour', 'Tous les choix sont déjà synchronisés');
      }

      if (errors > 0) {
        toast.error('Attention', `${errors} erreur(s) lors de la sync`);
      }
    } catch (error) {
      console.error('Erreur synchronisation:', error);
      toast.error('Erreur', 'Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const loadOptions = async () => {
    try {
      // FIREBASE DIRECT
      const result = await optionsService.getAll();
      setOptions(result.options || []);
    } catch (error) {
      console.error('Erreur chargement options:', error);
    }
  };

  const handleOpenModal = (option = null) => {
    setEditingOption(option);
    setShowModal(true);
  };


  const handleDeleteOption = async (optionId) => {
    if (!window.confirm('Supprimer cette option ?')) return;
    
    try {
      // FIREBASE DIRECT
      await optionsService.delete(optionId);
      await loadOptions();
      toast.success('Succès', 'Option supprimée !');
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur', 'Erreur lors de la suppression');
    }
  };

  const handleDuplicateOption = async (option) => {
    if (!window.confirm(`Dupliquer l'option "${option.name}" ?`)) return;
    
    try {
      const duplicatedOption = {
        ...option,
        name: `${option.name} (copie)`,
      };
      
      // Remove the id so backend creates a new one
      delete duplicatedOption.id;
      
      // FIREBASE DIRECT
      await optionsService.create(duplicatedOption);
      await loadOptions();
      toast.success('Succès', 'Option dupliquée avec succès !');
    } catch (error) {
      console.error('Erreur duplication:', error);
      toast.error('Erreur', 'Erreur lors de la duplication');
    }
  };


  return (
    <div>
      <Header 
        title="🎛️ Gestion des Options" 
        subtitle="Créez des options réutilisables pour vos produits"
      />
      
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Options disponibles ({options.length})</h2>
          <div className="flex items-center space-x-3">
            {/* Filtre options non utilisées */}
            <Button
              variant={showUnusedOptions ? 'default' : 'outline'}
              onClick={() => setShowUnusedOptions(!showUnusedOptions)}
              className={showUnusedOptions ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}
            >
              🚫 Non utilisées ({options.filter(opt => !isOptionUsed(opt.id)).length})
            </Button>
            {/* Sync Bibliothèque → Options (met à jour les images dans les options) */}
            <Button
              onClick={handleSyncFromLibrary}
              variant="outline"
              disabled={syncingFromLibrary}
              className="flex items-center space-x-2 bg-green-50 hover:bg-green-100 border-green-300"
            >
              <Download className={`w-4 h-4 ${syncingFromLibrary ? 'animate-bounce' : ''}`} />
              <span>{syncingFromLibrary ? 'Sync...' : '📚→ Mettre à jour images'}</span>
            </Button>
            <Button onClick={() => handleOpenModal()} className="flex items-center space-x-2">
              <Plus className="w-5 h-5" />
              <span>Nouvelle option</span>
            </Button>
          </div>
        </div>

        {options.length === 0 ? (
          <Card>
            <CardContent className="text-center py-20">
              <Plus className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-bold text-gray-600 mb-2">Aucune option</h3>
              <p className="text-gray-500 mb-4">
                Créez des options (taille, sauce, accompagnement...) pour enrichir vos produits
              </p>
              <Button onClick={() => handleOpenModal()}>
                <Plus className="w-4 h-4 mr-2" />
                Créer ma première option
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredOptions.map((option) => (
              <Card key={option.id} className="hover:shadow-xl transition-shadow">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
                  <CardTitle className="flex items-center justify-between">
                    <span className="text-lg font-bold">{option.name}</span>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleOpenModal(option)}
                        className="p-2 hover:bg-white rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4 text-blue-600" />
                      </button>
                      <button
                        onClick={() => handleDuplicateOption(option)}
                        className="p-2 hover:bg-white rounded-lg transition-colors"
                        title="Dupliquer"
                      >
                        <Copy className="w-4 h-4 text-green-600" />
                      </button>
                      <button
                        onClick={() => handleDeleteOption(option.id)}
                        className="p-2 hover:bg-white rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {option.description && (
                    <p className="text-sm text-gray-600">{option.description}</p>
                  )}
                  
                  {option.internal_comment && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                      <p className="text-xs font-semibold text-yellow-800 mb-1">📝 Commentaire interne</p>
                      <p className="text-sm text-yellow-700">{option.internal_comment}</p>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-2 text-sm">
                    <span className={`px-3 py-1 rounded-full font-bold ${
                      option.type === 'single' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {option.type === 'single' ? '📌 Choix unique' : '☑️ Choix multiple'}
                    </span>
                    {option.is_required && (
                      <span className="px-3 py-1 rounded-full bg-red-100 text-red-800 font-bold text-xs">
                        ⚠️ Obligatoire
                      </span>
                    )}
                  </div>

                  {option.type === 'multiple' && option.max_choices && (
                    <p className="text-sm text-gray-600">
                      Max: {option.max_choices} choix
                    </p>
                  )}

                  {option.price > 0 && (
                    <p className="text-sm font-bold text-green-600">
                      Prix base: +{option.price}€
                    </p>
                  )}

                  <div className="border-t pt-3 mt-3">
                    <p className="text-xs font-bold text-gray-500 mb-2">CHOIX ({option.choices?.length || 0}):</p>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {option.choices?.map((choice) => (
                        <div key={choice.id} className="bg-gray-50 p-2 rounded">
                          <div className="flex justify-between items-center text-sm">
                            <span>{choice.name}</span>
                            {choice.price > 0 && (
                              <span className="font-bold text-green-600">+{choice.price}€</span>
                            )}
                          </div>
                          {/* Badge sous-options */}
                          {choice.sub_options?.length > 0 && (
                            <div className="mt-1 flex items-center gap-1">
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                🔗 {choice.sub_options.length} sous-option{choice.sub_options.length > 1 ? 's' : ''}
                              </span>
                              <span className="text-xs text-gray-400">
                                ({choice.sub_options.map(so => so.name).join(', ')})
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal Création/Édition */}
      <OptionModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingOption(null);
        }}
        option={editingOption}
        onSuccess={loadOptions}
      />
    </div>
  );
};
